import { useState, useEffect, useCallback } from 'react';
import { Mail, Send, Building2, Wifi, FileText, Users, Star, ChevronUp, ChevronDown } from 'lucide-react';
import Modal from '../common/Modal';
import { useToast } from '../common/ToastContainer';
import emailTemplateService, { type EmailTemplate } from '../../services/emailTemplate.service';
import appDeviceService, { type DeviceGroup } from '../../services/appDevice.service';

interface InviteVPNUsersModalProps {
    isOpen: boolean;
    onClose: () => void;
    onInvite: (data: InviteData) => Promise<void>;
    tenantId: string;
    tenantName: string;
}

export interface InviteData {
    emails: string[];
    userType: number;
    tenantId: string;
    roleId?: string;
    emailTemplateId?: string;
    expiresInDays: number;
    deviceGroupIds: string[];
}

const InviteVPNUsersModal = ({
    isOpen,
    onClose,
    onInvite,
    tenantId,
    tenantName,
}: InviteVPNUsersModalProps) => {
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [emailsText, setEmailsText] = useState('');
    const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
    const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
    const [expiresInDays, setExpiresInDays] = useState(7);

    // Data for dropdowns
    const [templates, setTemplates] = useState<EmailTemplate[]>([]);
    const [deviceGroups, setDeviceGroups] = useState<DeviceGroup[]>([]);
    const [loadingData, setLoadingData] = useState(true);

    // Fetch templates and device groups on open
    useEffect(() => {
        if (isOpen) {
            setLoadingData(true);
            Promise.all([
                emailTemplateService.listContentTemplates()
                    .then((templatesData) => {
                        setTemplates(templatesData);
                        const vpnTemplate = templatesData.find(
                            (t) => t.name.toLowerCase().includes('vpn') || t.id === 'vpn-user-invitation'
                        );
                        if (vpnTemplate) {
                            setSelectedTemplateId(vpnTemplate.id);
                        } else {
                            const welcomeTemplate = templatesData.find(
                                (t) => t.name.toLowerCase().includes('welcome')
                            );
                            if (welcomeTemplate) {
                                setSelectedTemplateId(welcomeTemplate.id);
                            }
                        }
                    })
                    .catch(() => setTemplates([])),
                appDeviceService.listDeviceGroups()
                    .then((res) => setDeviceGroups(res.items || []))
                    .catch(() => setDeviceGroups([])),
            ]).finally(() => setLoadingData(false));
        }
    }, [isOpen, tenantId]);

    // Reset form when modal closes
    useEffect(() => {
        if (!isOpen) {
            setEmailsText('');
            setExpiresInDays(7);
            setSelectedGroupIds([]);
        }
    }, [isOpen]);

    const parseEmails = (text: string): string[] => {
        // Split by newlines, commas, or semicolons
        const emails = text
            .split(/[\n,;]+/)
            .map((e) => e.trim().toLowerCase())
            .filter((e) => e.length > 0);

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emails.filter((e) => emailRegex.test(e));
    };

    const moveGroupUp = useCallback((index: number) => {
        if (index <= 0) return;
        setSelectedGroupIds(prev => {
            const next = [...prev];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            return next;
        });
    }, []);

    const moveGroupDown = useCallback((index: number) => {
        setSelectedGroupIds(prev => {
            if (index >= prev.length - 1) return prev;
            const next = [...prev];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            return next;
        });
    }, []);

    const handleSubmit = async () => {
        const emails = parseEmails(emailsText);

        if (emails.length === 0) {
            toast.error('No Valid Emails', 'Please enter at least one valid email address');
            return;
        }

        if (selectedGroupIds.length === 0) {
            toast.error('Device Group Required', 'Please select at least one device group');
            return;
        }

        // Check for duplicates
        const uniqueEmails = [...new Set(emails)];
        if (uniqueEmails.length !== emails.length) {
            toast.warning('Duplicates Removed', `${emails.length - uniqueEmails.length} duplicate emails were removed`);
        }

        setLoading(true);
        try {
            await onInvite({
                emails: uniqueEmails,
                userType: 3, // Always VPN User (type 3)
                tenantId,
                emailTemplateId: selectedTemplateId || undefined,
                expiresInDays,
                deviceGroupIds: selectedGroupIds,
            });
            // Success toast is emitted by the parent's onInvite handler —
            // it has richer per-result detail (provisioned vs already_exists
            // vs invalid).
            onClose();
        } catch (error) {
            console.error('Failed to send invitations:', error);
            toast.error('Invitation Failed', 'Failed to send invitations. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const emailCount = parseEmails(emailsText).length;

    const footer = (
        <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">
                {emailCount} valid email{emailCount !== 1 ? 's' : ''} entered
            </span>
            <div className="flex items-center space-x-3">
                <button onClick={onClose} className="btn-secondary" disabled={loading}>
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    className="btn-primary inline-flex items-center space-x-2"
                    disabled={loading || emailCount === 0}
                >
                    <Send className="w-4 h-4" />
                    <span>{loading ? 'Sending...' : 'Send Invitations'}</span>
                </button>
            </div>
        </div>
    );

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Invite VPN Users"
            size="2xl"
            footer={footer}
        >
            <div className="space-y-6">
                {/* Tenant Info Banner */}
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 flex items-center space-x-3">
                    <Building2 className="w-5 h-5 text-primary-600" />
                    <div>
                        <p className="text-sm font-medium text-primary-900">
                            Inviting VPN users to: <strong>{tenantName}</strong>
                        </p>
                        <p className="text-xs text-primary-700">
                            Users will be able to connect via the VPN app after registration
                        </p>
                    </div>
                </div>

                {/* Email Addresses */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span>VPN User Email Addresses</span>
                        <span className="text-red-500">*</span>
                    </label>
                    <textarea
                        value={emailsText}
                        onChange={(e) => setEmailsText(e.target.value)}
                        placeholder="Enter email addresses (one per line, or separated by commas)&#10;&#10;user1@company.com&#10;user2@company.com&#10;user3@company.com"
                        className="input-base w-full h-32 font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Enter one email per line, or separate with commas. Invalid emails will be ignored.
                    </p>
                </div>

                {/* Info Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
                    <Wifi className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                        <p className="text-sm font-medium text-blue-900">VPN Access</p>
                        <p className="text-xs text-blue-700">
                            All invited users will be registered as VPN Users (standard client access).
                            They will not have administrative access to the VSN+ dashboard.
                        </p>
                    </div>
                </div>


                {/* Email Template */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span>Welcome Email Template</span>
                    </label>
                    <select
                        value={selectedTemplateId}
                        onChange={(e) => setSelectedTemplateId(e.target.value)}
                        className="input-base w-full"
                        disabled={loadingData}
                    >
                        <option value="">Default welcome email</option>
                        {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                                {template.name}
                            </option>
                        ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                        Select a template for the welcome email, or use the system default
                    </p>
                </div>

                {/* Device Groups (Required) */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center space-x-2">
                        <Users className="w-4 h-4 text-gray-400" />
                        <span>Device Groups</span>
                        <span className="text-red-500">*</span>
                    </label>
                    {deviceGroups.length > 0 ? (
                        <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                            {deviceGroups.map((group) => {
                                const isChecked = selectedGroupIds.includes(group.id);
                                const capacityPct = group.max_devices ? ((group.device_count || 0) / group.max_devices) * 100 : 0;
                                return (
                                    <label
                                        key={group.id}
                                        className={`flex items-center px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors ${isChecked ? 'bg-primary-50' : ''}`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => {
                                                setSelectedGroupIds(prev =>
                                                    isChecked ? prev.filter(id => id !== group.id) : [...prev, group.id]
                                                );
                                            }}
                                            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500 mr-3"
                                            disabled={loadingData}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center space-x-2">
                                                <span className="text-sm font-medium text-gray-900 truncate">{group.name}</span>
                                                {group.edge_id ? (
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                                                        group.edge_type === 'mtge' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                                    }`}>
                                                        {group.edge_type === 'mtge' ? 'MTGE' : 'Edge'}
                                                    </span>
                                                ) : (
                                                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">Unbound</span>
                                                )}
                                            </div>
                                            {group.max_devices && (
                                                <span className={`text-xs ${capacityPct > 95 ? 'text-red-600' : capacityPct > 80 ? 'text-amber-600' : 'text-gray-500'}`}>
                                                    {group.device_count || 0}/{group.max_devices} devices
                                                </span>
                                            )}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 text-sm text-amber-800">
                            No device groups found. Create a device group first before inviting VPN users.
                        </div>
                    )}

                    {/* Selected groups ordering (primary designation) */}
                    {selectedGroupIds.length > 1 && (
                        <div className="mt-2 border border-gray-200 rounded-lg divide-y divide-gray-100">
                            <div className="px-3 py-1.5 bg-gray-50 text-xs font-medium text-gray-500">
                                Selected order (first = primary default group)
                            </div>
                            {selectedGroupIds.map((gId, index) => {
                                const group = deviceGroups.find(g => g.id === gId);
                                if (!group) return null;
                                return (
                                    <div key={gId} className="flex items-center px-3 py-1.5">
                                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                                            {index === 0 && (
                                                <Star className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" fill="currentColor" />
                                            )}
                                            <span className={`text-sm truncate ${index === 0 ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                                                {group.name}
                                            </span>
                                            {index === 0 && (
                                                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">
                                                    Primary
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center space-x-0.5">
                                            <button
                                                type="button"
                                                onClick={() => moveGroupUp(index)}
                                                disabled={index === 0}
                                                className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                            >
                                                <ChevronUp className="w-3.5 h-3.5 text-gray-500" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => moveGroupDown(index)}
                                                disabled={index === selectedGroupIds.length - 1}
                                                className="p-0.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                                            >
                                                <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    <p className="text-xs text-gray-500 mt-1">
                        Select at least one device group. {selectedGroupIds.length > 1 ? 'Use arrows to reorder — the first group is the primary (default) group for registration.' : 'Users will register devices to the selected group.'}
                    </p>
                </div>

                {/* Expiration */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Invitation Expires In
                    </label>
                    <div className="flex items-center space-x-2">
                        <input
                            type="number"
                            value={expiresInDays}
                            onChange={(e) => setExpiresInDays(Math.max(1, Math.min(30, parseInt(e.target.value) || 7)))}
                            min={1}
                            max={30}
                            className="input-base w-20"
                        />
                        <span className="text-sm text-gray-600">days</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        Users must complete registration within this time (1-30 days)
                    </p>
                </div>

                {/* Info Note */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800">
                        <strong>What happens next:</strong>
                    </p>
                    <ol className="text-sm text-blue-700 list-decimal list-inside mt-2 space-y-1">
                        <li>Each user receives a welcome email with registration instructions</li>
                        <li>They click the link to set up their account credentials</li>
                        <li>After setup, they can use their email to log in to the SecureLink VPN app</li>
                    </ol>
                </div>
            </div>
        </Modal>
    );
};

export default InviteVPNUsersModal;
